import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { cn } from '../../../utils/cn';

const MessagingPanel = ({ compact = false }) => {
  const [selectedThread, setSelectedThread] = useState(null);
  const [messageText, setMessageText] = useState('');

  const [threads, setThreads] = useState([
    {
      id: 1,
      patient: 'Sarah Johnson',
      mrn: 'MRN-2024-789456',
      subject: 'Medication adjustment discussion',
      participants: ['Dr. Michael Chen', 'Dr. Sarah Williams', 'Nurse Emily Roberts'],
      lastMessage: 'Agreed on new dosage. Will monitor closely.',
      lastMessageBy: 'Dr. Sarah Williams',
      lastMessageTime: '10 min ago',
      unreadCount: 2,
      priority: 'high',
      messages: [
        {
          id: 1,
          sender: 'Dr. Michael Chen',
          message: 'Lab results show elevated potassium. Recommend adjusting medication.',
          timestamp: '2026-01-14 09:30',
          read: true
        },
        {
          id: 2,
          sender: 'Dr. Sarah Williams',
          message: 'Reviewed labs. Suggest reducing dose by 50%. Will coordinate with pharmacy.',
          timestamp: '2026-01-14 09:45',
          read: true
        },
        {
          id: 3,
          sender: 'Nurse Emily Roberts',
          message: 'Patient education completed. Monitoring vitals every 4 hours.',
          timestamp: '2026-01-14 10:15',
          read: false
        },
        {
          id: 4,
          sender: 'Dr. Sarah Williams',
          message: 'Agreed on new dosage. Will monitor closely.',
          timestamp: '2026-01-14 10:30',
          read: false
        }
      ]
    },
    {
      id: 2,
      patient: 'Sarah Johnson',
      mrn: 'MRN-2024-789456',
      subject: 'Discharge planning coordination',
      participants: ['Dr. Michael Chen', 'Nurse Emily Roberts', 'Case Management'],
      lastMessage: 'Home health services confirmed for next week.',
      lastMessageBy: 'Case Management',
      lastMessageTime: '1 hour ago',
      unreadCount: 0,
      priority: 'normal',
      messages: [
        {
          id: 1,
          sender: 'Dr. Michael Chen',
          message: 'Patient ready for discharge tomorrow. Need to arrange home health.',
          timestamp: '2026-01-14 08:00',
          read: true
        },
        {
          id: 2,
          sender: 'Case Management',
          message: 'Home health services confirmed for next week.',
          timestamp: '2026-01-14 09:00',
          read: true
        }
      ]
    },
    {
      id: 3,
      patient: 'Robert Anderson',
      mrn: 'MRN-2024-456123',
      subject: 'Nephrology consultation follow-up',
      participants: ['Dr. Lisa Thompson', 'Dr. James Wilson', 'Nurse David Kim'],
      lastMessage: 'Dialysis schedule updated per nephrologist recommendations.',
      lastMessageBy: 'Nurse David Kim',
      lastMessageTime: '3 hours ago',
      unreadCount: 1,
      priority: 'normal',
      messages: [
        {
          id: 1,
          sender: 'Dr. Lisa Thompson',
          message: 'Requesting nephrology input on dialysis schedule.',
          timestamp: '2026-01-14 06:00',
          read: true
        },
        {
          id: 2,
          sender: 'Dr. James Wilson',
          message: 'Recommend increasing frequency to 3x per week.',
          timestamp: '2026-01-14 07:30',
          read: true
        },
        {
          id: 3,
          sender: 'Nurse David Kim',
          message: 'Dialysis schedule updated per nephrologist recommendations.',
          timestamp: '2026-01-14 08:00',
          read: false
        }
      ]
    }
  ]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'text-error';
      case 'high':
        return 'text-warning';
      case 'normal':
        return 'text-primary';
      default:
        return 'text-muted-foreground';
    }
  };

  const displayThreads = compact ? threads?.slice(0, 3) : threads;

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-1">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="MessageSquare" size={20} className="text-primary" />
            HIPAA-Compliant Messaging
          </h2>
          <Button variant="default" size="sm" iconName="Plus">
            New Thread
          </Button>
        </div>
      </div>

      <div className={cn("grid", !compact && selectedThread ? "lg:grid-cols-2" : "grid-cols-1")}>
        {/* Thread List */}
        <div className={cn("border-r border-border", compact ? "p-4" : "")}>
          <div className={cn(compact ? "space-y-2" : "divide-y divide-border")}>
            {displayThreads?.map((thread) => (
              <div
                key={thread?.id}
                onClick={() => !compact && setSelectedThread(thread)}
                className={cn(
                  "p-4 transition-colors",
                  !compact && "cursor-pointer hover:bg-muted/50",
                  selectedThread?.id === thread?.id && "bg-muted/50",
                  compact && "bg-background rounded-lg border border-border"
                )}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground truncate">{thread?.subject}</h3>
                      {thread?.unreadCount > 0 && (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                          {thread?.unreadCount}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {thread?.patient} • {thread?.mrn}
                    </p>
                  </div>
                  <Icon name="AlertCircle" size={16} className={getPriorityColor(thread?.priority)} />
                </div>

                <p className="text-sm text-foreground mb-2 line-clamp-1">{thread?.lastMessage}</p>

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{thread?.lastMessageBy}</span>
                  <span>{thread?.lastMessageTime}</span>
                </div>

                <div className="flex items-center gap-1 mt-2 flex-wrap">
                  {thread?.participants?.slice(0, 3)?.map((participant, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-medium"
                      title={participant}
                    >
                      {participant?.split(' ')?.map(n => n?.[0])?.join('')}
                    </span>
                  ))}
                  {thread?.participants?.length > 3 && (
                    <span className="text-xs text-muted-foreground ml-1">
                      +{thread?.participants?.length - 3}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {compact && threads?.length > 3 && (
            <div className="mt-4">
              <Button variant="outline" fullWidth iconName="ArrowRight" iconPosition="right">
                View All Messages
              </Button>
            </div>
          )}
        </div>

        {/* Message Thread Detail */}
        {!compact && selectedThread && (
          <div className="flex flex-col h-[600px]">
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold text-foreground mb-1">{selectedThread?.subject}</h3>
              <p className="text-sm text-muted-foreground">
                {selectedThread?.patient} • {selectedThread?.mrn}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-muted-foreground">Participants:</span>
                {selectedThread?.participants?.map((participant, idx) => (
                  <span key={idx} className="text-xs text-foreground">
                    {participant}{idx < selectedThread?.participants?.length - 1 ? ',' : ''}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {selectedThread?.messages?.map((msg) => (
                <div key={msg?.id} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-semibold text-primary">
                      {msg?.sender?.split(' ')?.map(n => n?.[0])?.join('')}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-foreground text-sm">{msg?.sender}</span>
                      <span className="text-xs text-muted-foreground">{msg?.timestamp}</span>
                      {msg?.read && (
                        <Icon name="CheckCheck" size={14} className="text-success" />
                      )}
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-sm text-foreground">{msg?.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-border">
              <div className="flex gap-2">
                <Input
                  value={messageText}
                  onChange={(e) => setMessageText(e?.target?.value)}
                  placeholder="Type your message..."
                  className="flex-1"
                />
                <Button variant="ghost" size="icon" iconName="Paperclip" />
                <Button variant="default" iconName="Send">
                  Send
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                <Icon name="Lock" size={12} className="inline mr-1" />
                HIPAA-compliant encrypted messaging
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagingPanel;